import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';

/**
 * The account menu — who you are signed in as, your profile, and the way out.
 *
 * The chip in figure 7.2 states the user and the role and does nothing; this
 * keeps that statement and hangs the two account actions off it, which is
 * where an operator looks for them. A menu rather than two more items in the
 * top bar: sign-out beside a live alert count is an accident waiting to
 * happen.
 *
 * Closing on Escape and on an outside click, and returning focus to the
 * trigger, is the whole of what makes this usable without a mouse.
 */
export function UserMenu({
  displayName,
  roleLabel,
}: {
  displayName: string;
  roleLabel: string | null;
}) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  async function endSession() {
    setOpen(false);
    await signOut();
    // Sign-out lands on the sign-in screen, not on a guard's redirect: the
    // operator asked to leave and should see that it happened.
    navigate('/login', { replace: true });
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        /*
         * Explicit, because below `sm` the only visible content is the
         * initials and those are `aria-hidden` — without this the button
         * would lose its accessible name at exactly the width where a
         * screen-reader user has least other context.
         */
        aria-label={roleLabel ? `${displayName} · ${roleLabel}` : displayName}
        className="flex shrink-0 items-center gap-2 rounded border border-pv-line bg-pv-surface px-1 py-0.5 text-pv-ink hover:border-pv-primary sm:pr-2"
      >
        <span
          aria-hidden="true"
          className="flex h-[22px] w-[22px] items-center justify-center bg-pv-band text-[0.625rem] font-bold text-white"
        >
          {initials(displayName)}
        </span>
        {/*
         * Below `sm` the initials are the chip. The name and role were pushing
         * the whole group off the right edge of a 390 px bar, and they are both
         * repeated in the menu this button opens — so nothing is lost by
         * dropping them, while the accessible name keeps them for a screen
         * reader at every width.
         */}
        <span className="hidden max-w-[24ch] truncate text-[0.75rem] font-semibold sm:inline">
          {displayName}
          {roleLabel ? <span className="font-normal text-pv-muted"> · {roleLabel}</span> : null}
        </span>
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m6 8 4 4 4-4" />
        </svg>
      </button>

      {open ? (
        <div
          role="menu"
          aria-label="Account"
          className="absolute right-0 z-40 mt-2 w-[240px] overflow-hidden pv-card p-1 text-pv-ink shadow-[var(--pv-shadow-lg)]"
        >
          <div className="px-3 py-2">
            <p className="font-semibold">{displayName}</p>
            {roleLabel ? <p className="text-dense text-pv-muted">{roleLabel}</p> : null}
          </div>
          <div className="h-px bg-pv-line/70" />
          <Link
            role="menuitem"
            to="/profile"
            onClick={() => setOpen(false)}
            className="block rounded px-3 py-2 font-medium hover:bg-pv-tint"
          >
            Your profile
          </Link>
          <button
            role="menuitem"
            type="button"
            onClick={() => void endSession()}
            className="block w-full rounded px-3 py-2 text-left font-medium text-pv-danger hover:bg-pv-tint"
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

function initials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}
